import { Component, EventEmitter, Input, OnInit, Output, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Shift, ShiftEmployee, ShiftFormEmployee, WORKING_DAYS, TimezoneOption } from 'src/app/models/shift.model';
import { ShiftService } from 'src/app/Services/shift.service';
import { FormControl } from '@angular/forms';

@Component({
    selector: 'app-shift-form',
    templateUrl: './shift-form.component.html',
    styleUrls: ['./shift-form.component.css'],
    standalone: false
})
export class ShiftFormComponent implements OnInit, OnDestroy, OnChanges {
  @Input() editingShift: any = null;
  @Input() isEditMode = false;
  @Input() employees: ShiftEmployee[] = [];
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();

  shiftForm: FormGroup;
  isOvernight = false;
  timezones: TimezoneOption[] = [];
  filteredTimezones: TimezoneOption[] = [];
  workingDays = WORKING_DAYS;
  selectedDays: number[] = [1, 2, 3, 4, 5];
  timezoneSearchCtrl = new FormControl('');
  isLoadingTimezones = false;
  private timezoneSearchSub: Subscription;
  shiftDuration = '';

  shiftFormEmployees: ShiftFormEmployee[] = [];
  isLoadingEmployees = false;
  selectedEmployeeIds: number[] = [];
  employeeSearchQuery = '';

  get filteredEmployees(): ShiftFormEmployee[] {
    const q = this.employeeSearchQuery.toLowerCase().trim();
    if (!q) return this.shiftFormEmployees;
    return this.shiftFormEmployees.filter(e => e.name.toLowerCase().includes(q));
  }

  private formSub: Subscription;

  constructor(
    private fb: FormBuilder,
    private shiftService: ShiftService
  ) {}

  ngOnInit(): void {
    this.shiftForm = this.fb.group({
      shift_name: ['', [Validators.required, Validators.minLength(2)]],
      timezone: ['Asia/Kolkata', Validators.required],
      start_time: ['09:00', Validators.required],
      end_time: ['17:00', Validators.required],
      buffer_start_minutes: [0, [Validators.min(0), Validators.max(120)]],
      buffer_end_minutes: [0, [Validators.min(0), Validators.max(120)]],
      strict_window: [false],
    });

    this.formSub = this.shiftForm.valueChanges.subscribe(() => {
      this.detectOvernight();
      this.calculateDuration();
    });

    // Load timezones from API
    this.loadTimezones();

    // Pre-fill form if in edit mode; load employees in create mode
    if (this.isEditMode && this.editingShift) {
      this.prefillForm();
    } else if (!this.isEditMode) {
      this.loadEmployeesForShift();
    }

    // Initial calculations
    this.detectOvernight();
    this.calculateDuration();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editingShift'] && this.isEditMode && this.editingShift) {
      setTimeout(() => {
        this.prefillForm();
      }, 0);
    }
    if (changes['employees'] && !this.isEditMode) {
      this.loadEmployeesForShift();
    }
  }

  prefillForm(): void {
    if (!this.editingShift) return;

    this.shiftForm.patchValue({
      shift_name: this.editingShift.shift_name,
      timezone: this.editingShift.timezone,
      start_time: this.editingShift.start_time?.substring(0, 5) || '09:00',
      end_time: this.editingShift.end_time?.substring(0, 5) || '17:00',
      buffer_start_minutes: this.editingShift.buffer_start_minutes ?? 0,
      buffer_end_minutes: this.editingShift.buffer_end_minutes ?? 0,
      strict_window: false,
    });

    this.selectedDays = this.editingShift.working_days || [1, 2, 3, 4, 5];
    this.isOvernight = this.editingShift.is_overnight || false;
    this.loadEmployeesForShift();
  }

  private loadEmployeesForShift(): void {
    if (this.isEditMode && this.editingShift?.id) {
      this.isLoadingEmployees = true;
      this.shiftService.getShiftEmployees(this.editingShift.id).subscribe({
        next: (employees) => {
          this.shiftFormEmployees = employees;
          this.selectedEmployeeIds = employees
            .filter(e => e.on_this_shift)
            .map(e => e.user_id);
          this.isLoadingEmployees = false;
        },
        error: () => {
          this.isLoadingEmployees = false;
        }
      });
    } else {
      this.shiftFormEmployees = this.employees.map(e => ({
        user_id: e.id,
        name: e.name,
        on_this_shift: false,
        current_shift_id: null,
        current_shift_name: null,
      }));
      this.selectedEmployeeIds = [];
    }
  }

  ngOnDestroy(): void {
    this.formSub?.unsubscribe();
    this.timezoneSearchSub?.unsubscribe();
  }

  loadTimezones(): void {
    this.isLoadingTimezones = true;
    this.shiftService.getTimezones().subscribe({
      next: (response) => {
        this.timezones = response.timezones;
        this.filteredTimezones = response.timezones;
        this.isLoadingTimezones = false;
        
        // Setup timezone search only after timezones are loaded
        this.timezoneSearchSub = this.timezoneSearchCtrl.valueChanges.subscribe(query => {
          this.filterTimezones(query || '');
        });
      },
      error: () => {
        this.isLoadingTimezones = false;
        // Fallback to default timezone if API fails
        this.timezones = [{ value: 'Asia/Kolkata', label: 'UTC+05:30 — Asia/Kolkata', offset: 'UTC+05:30' }];
        this.filteredTimezones = this.timezones;
        
        // Setup timezone search even with fallback
        this.timezoneSearchSub = this.timezoneSearchCtrl.valueChanges.subscribe(query => {
          this.filterTimezones(query || '');
        });
      }
    });
  }

  filterTimezones(query: string): void {
    const q = query.toLowerCase();
    this.filteredTimezones = this.timezones.filter(tz => 
      tz.label.toLowerCase().includes(q) || tz.value.toLowerCase().includes(q)
    );
  }

  isEmployeeSelected(userId: number): boolean {
    return this.selectedEmployeeIds.includes(userId);
  }

  toggleEmployee(userId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedEmployeeIds.includes(userId)) {
        this.selectedEmployeeIds = [...this.selectedEmployeeIds, userId];
      }
    } else {
      this.selectedEmployeeIds = this.selectedEmployeeIds.filter(id => id !== userId);
    }
  }

  toggleDay(day: number): void {
    const idx = this.selectedDays.indexOf(day);
    if (idx > -1) {
      this.selectedDays = this.selectedDays.filter(d => d !== day);
    } else {
      this.selectedDays = [...this.selectedDays, day].sort((a, b) => a - b);
    }
  }

  isDaySelected(day: number): boolean {
    return this.selectedDays.includes(day);
  }

  private parseTimeToMinutes(timeStr: string): number {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  }

  private detectOvernight(): void {
    const v = this.shiftForm?.value;
    if (!v) return;
    const start = this.parseTimeToMinutes(v.start_time);
    const end = this.parseTimeToMinutes(v.end_time);
    this.isOvernight = end <= start;
  }

  private calculateDuration(): void {
    const v = this.shiftForm?.value;
    if (!v) return;
    const start = this.parseTimeToMinutes(v.start_time);
    let end = this.parseTimeToMinutes(v.end_time);
    if (this.isOvernight) end += 24 * 60;
    const diff = end - start;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    this.shiftDuration = m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  onSubmit(): void {
    console.log('Form submit called:', { isEditMode: this.isEditMode, editingShift: this.editingShift }); // Debug log
    
    if (this.shiftForm.invalid) {
      console.log('Form invalid:', this.shiftForm.errors); // Debug log
      this.shiftForm.markAllAsTouched();
      return;
    }
    const v = this.shiftForm.value;
    const formData = {
      shift_name: v.shift_name.trim(),
      timezone: v.timezone,
      start_time: v.start_time + ':00',
      end_time: v.end_time + ':00',
      is_overnight: this.isOvernight,
      working_days: this.selectedDays,
      // buffer_start_minutes: v.buffer_start_minutes ?? 0,
      // buffer_end_minutes: v.buffer_end_minutes ?? 0,
      strict_window: false,
      employee_ids: this.selectedEmployeeIds,
    };
    console.log('Emitting form data:', formData); // Debug log
    this.formSubmit.emit(formData);
  }

  onCancel(): void {
    this.formCancel.emit();
  }
}
